import { useState } from 'react'

import {
  Box,
  Button,
  List,
  Tag,
  Text,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'
import {
  CompensationsPayrollConceptFragment,
  CompensationsPayrollConceptsDocument,
  CompensationsTogglePayrollConceptStatusDocument,
} from 'generated/resources/graphql'
import { CompensationsCreatePayrollConceptStatusDocument } from 'generated/resources/graphql'
import { isNil } from 'lodash'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import EditConceptModal from 'modules/compensations/pages/settings/PayrollConcepts/EditConceptModal'
import * as styles from 'modules/compensations/pages/settings/PayrollConcepts/index.vanilla.css'

interface ConceptBlockProps {
  concept: CompensationsPayrollConceptFragment
  legacyPayroll?: boolean
}

const ConceptBlock = ({ concept, legacyPayroll = false }: ConceptBlockProps) => {
  const [showEditModal, setShowEditModal] = useState<boolean>(false)

  const [create] = useMutation(CompensationsCreatePayrollConceptStatusDocument, {
    extractorFn: (result) => result.compensationsMutations.createCompensationsPayrollConceptStatus,
  })

  const [toggle] = useMutation(CompensationsTogglePayrollConceptStatusDocument, {
    extractorFn: (result) => result.compensationsMutations.toggleCompensationsPayrollConceptStatus,
  })

  const onToggleCreate = async (newValue: boolean) => {
    const result = await create({
      variables: {
        companyId: concept.companyId,
        payrollConceptId: concept.id,
        enabled: newValue,
      },
      refetchQueries: [CompensationsPayrollConceptsDocument],
    })

    const handled = handleResult(result, () => undefined)
    return handled.success
  }

  const onToggleUpdate = async () => {
    if (isNil(concept.compensationsStatus?.id)) {
      throw new Error('No id')
    }

    const result = await toggle({
      variables: {
        id: concept.compensationsStatus.id,
      },
      refetchQueries: [CompensationsPayrollConceptsDocument],
    })

    const handled = handleResult(result, () => undefined)
    return handled.success
  }

  const onToggleConcept = async (newValue: boolean) => {
    const success = await (concept.compensationsStatus
      ? onToggleUpdate()
      : onToggleCreate(newValue))

    track(trackingEvents.compensations.togglePayrollConcept, {
      id: concept.id,
      enabled: newValue,
    })

    if (success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.save_error'),
      })
    }
  }

  return (
    <>
      <List.Item key={concept.id} className={styles.listSubitem}>
        <List.Title>
          <Box paddingLeft="s24" flexDirection="row" gap="s8">
            <Text.Normal color="grey1000">{concept.label}</Text.Normal>
            {!concept.default && <Tag>{i18n.t('compensations.settings.concepts.custom')}</Tag>}
          </Box>
        </List.Title>
        <List.ActionItem>
          <Box flexDirection="row" gap="s8">
            {!legacyPayroll && (
              <TogglerCheckbox
                onChange={onToggleConcept}
                value={concept.compensationsStatus?.enabled ?? true}
                aria-label={i18n.t('finance.settings.accounting.custom_categories.actions.toggle')}
              />
            )}
            <Button
              type="secondary"
              iconName="arrow-right"
              onClick={() => setShowEditModal(true)}
            ></Button>
          </Box>
        </List.ActionItem>
      </List.Item>
      {showEditModal && (
        <EditConceptModal
          readonly={concept.default}
          concept={concept}
          onSuccess={() => setShowEditModal(false)}
          onClose={() => setShowEditModal(false)}
          legacyPayroll={legacyPayroll}
        />
      )}
    </>
  )
}

export default ConceptBlock
